import {useEffect, useState} from "react";
import {GetConfigByName} from "../../../../reduxToolkit/authentication/AuthenticatonApi";
import {SYSTEM_CONFIG_LIST} from "../../Setting/Model/SettingConfigModel";
import {OpenDoorByDeviceOtp} from "../../../../reduxToolkit/Devices/DevicesApi";
import {ErrorCode} from "../../../../Apis/ErrorCode";
import {errorMsg} from "../../../../Apis/functions";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 05 de 05 de 2026
 */

/**
 * Custom hook used to manage OTP validation
 * for opening a device door remotely.
 *
 * This hook handles:
 * - OTP validation.
 * - OTP request state.
 * - Permission validation.
 * - Loading state management.
 * - System configuration retrieval.
 * - Door access verification flow.
 *
 * @function OpenDoorByDeviceModalApp
 *
 * @param {Object} item - Device information object.
 * @param {number|string} item.id - Device identifier.
 *
 * @returns {Object} Hook response object.
 * @returns {Function} returns.handleVerify - Executes OTP verification flow.
 * @returns {boolean|null} returns.isValidOtp - Indicates OTP validation result.
 * @returns {Function} returns.setIsValidOtp - Updates OTP validation state.
 * @returns {boolean} returns.loading - Indicates whether request is in progress.
 * @returns {boolean} returns.showOtp - Indicates whether OTP validation is enabled.
 * @returns {string} returns.otp - Current OTP value.
 * @returns {Function} returns.setOtp - Updates OTP value.
 * @returns {Function} returns.t - Translation function.
 * @returns {boolean} returns.emptyFields - Indicates validation errors.
 * @returns {Function} returns.setEmptyFields - Updates validation error state.
 * @returns {boolean} returns.pass - Indicates whether access was granted.
 */
export const OpenDoorByDeviceModalApp = (item) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(); // i18n translation hook
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidOtp, setIsValidOtp] = useState(null);
    const [pass, setPass] = useState(false);
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [showOtp, setShowOtp] = useState(false);


    useEffect(() => {
        // Fetch system config to determine if OTP is required
        dispatch(GetConfigByName(SYSTEM_CONFIG_LIST.enableOtp)).then(({payload: {data: { data },},}) => {
                setShowOtp(data);
            },
        );
    }, []);

    /**
     * Validates OTP and executes door opening request.
     *
     * Validation rules:
     * - OTP must exist.
     * - OTP length must be exactly 6 digits.
     * - OTP must contain only numeric values.
     */
    const handleVerify = () => {

        if (!otp || otp.length !== 6) {
            setIsValidOtp(false);
            setEmptyFields(true);
            return;
        }

        if (!/^\d{6}$/.test(otp)) {
            setIsValidOtp(false);
            setEmptyFields(true);
            return;
        }
        setLoading(true);


        dispatch(OpenDoorByDeviceOtp({ deviceId: item.id, code: otp }))
            .then(({payload: {data:{data,success,code}}}) => {
                const errorCode = code;
                setLoading(false);
                if (success) {

                    setIsValidOtp(true);
                    setEmptyFields(false);
                    setPass(true);

                    setOtp('');
                    return;
                }
                if (errorCode === ErrorCode.INVALID_CODE) {
                    errorMsg(errorCode);
                    setIsValidOtp(false);
                    setEmptyFields(true);

                    return;
                }
                if (errorCode === ErrorCode.ACCESS_DENIED_PERMISSION) {
                    setIsValidOtp(true);
                    setEmptyFields(false);
                    setPass(false);

                    return;
                }
                if (errorCode) {
                    errorMsg(errorCode);
                    setIsValidOtp(false);
                    setEmptyFields(true);
                }
            });
    };

    return {
        handleVerify,
        isValidOtp,
        setIsValidOtp,
        loading,
        showOtp,
        otp,
        setOtp,
        t,
        emptyFields,
        setEmptyFields,
        pass
    }
}
